import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import {
  SecondaryButton,
  SecondaryDangerButton,
} from "@/components/Buttons.jsx";
import { getOrderByID } from "@/api/index.js";
import { refundOrder } from "@/api/orders.js";
import { addToast, setSelectedMerchant } from "@/store/app/action.js";
import {
  findMerchantLocationByStoreId,
  formatPhoneNumber,
  formatValue,
  OrderStatusInverseEnum,
  statusColor,
} from "@/utils/Utils.js";

import Tooltip from "@/components/Tooltip.jsx";
import { useSmallSize } from "@/utils/UseSmallSize.jsx";
import Badge from "@/components/Badges.jsx";
import CanView from "@/components/CanView.jsx";
import {
  FaTruck,
  FaDollarSign,
  FaList,
  FaMagnifyingGlass,
  FaComment,
  FaArrowUpRightFromSquare,
  FaCrown,
  FaTriangleExclamation,
  FaCircleCheck,
  FaCircleXmark,
  FaCircleInfo,
  FaTag,
  FaClock,
} from "react-icons/fa6";
import cloverLogo from "@/pages/externalFeeds/settings/sections/POSConnection/logos/clover_small.svg";

const posProviderIcons = {
  clover: cloverLogo,
};
import { PiDetectiveBold } from "react-icons/pi";
import moment from "moment/moment.js";
import PaymentModal from "@/pages/orders/PaymentModal.jsx";
import CustomerDetailModal from "@/components/CustomerDetailModal.jsx";
import { useTheme } from "@/hooks/useTheme.js";
import { getStableFakeName } from "@/utils/fakeName.js";
import OrderHistoryTimeline from "@/components/orders/OrderHistoryTimeline.jsx";

const calc = (field, order) => {
  let withheld =
    (order.direct_delivery_fee_actual || 0) +
    (order.service_fee || 0) +
    (order.retailer_paid_service_fee || 0);

  if (order.order_type === "delivery" && !order.employee_delivery) {
    withheld += order.tip_amount || 0;
  }

  // Calculate total refunded amount from payments (reused across calculations)
  // Only count refunds that have a corresponding successful payment with same payment_intent_id

  const paymentField = order.payments?.[0]?.frame_charge_intent_id ? "frame_charge_intent_id" : order.payments?.[0]?.payment_intent_id ? "payment_intent_id" : "nmi_transaction_id";
  const totalRefunded =
    order.payments
      ?.filter((payment) => {
        if (payment.charge_status !== -1) return false; // Must be a refund

        // if (paymentField === "nmi_transaction_id") {
        //   return true;
        // }

        // Check if there's a corresponding successful payment with same payment_intent_id
        const hasSuccessfulPayment = order.payments?.some(
          (otherPayment) =>
            otherPayment[paymentField] === payment[paymentField] &&
            otherPayment.charge_status === 2
        );

        return hasSuccessfulPayment;
      })
      ?.reduce((sum, payment) => sum + (payment.hold_amount || 0), 0) || 0;
  const totalRefundedDollars = totalRefunded / 100;

  switch (field) {
    case "total_withheld":
      return withheld * -1;
    case "total_refunded":
      if (order.order_status === 5) {
        return order.total;
      }
      return totalRefundedDollars * -1; // Return as negative value
    case "total_paid_out":
      if (order.order_status === 5) {
        return 0;
      }
      return (
        order.total -
        withheld -
        order.gateway_transaction_fees_cents / 100 -
        totalRefundedDollars
      );
    case "gateway_fee":
      return (order.gateway_transaction_fees_cents / 100) * -1;
  }
};
const PastOrderModal = ({ setModalOpen, modalOpen, orderID, forceReload }) => {
  const dispatch = useDispatch();
  const { selectedLocation, merchants, isDemoUser } = useSelector(
    (state) => state.app
  );
  const [orderFetchKey, setOrderFetchKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [order, setOrder] = useState(null);
  const isSmallSize = useSmallSize();
  const [orderLineItems, setOrderLineItems] = useState(null);
  const [currentView, setCurrentView] = useState("order");
  const [viewPaymentIntentId, setViewPaymentIntentId] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [refundError, setRefundError] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [viewFraudEval, setViewFraudEval] = useState(null);
  const [withholdingWarningModalOpen, setWithholdingWarningModalOpen] = useState(false);
  const [pendingRefundAmount, setPendingRefundAmount] = useState(null);
  const [withholdingConfirmed, setWithholdingConfirmed] = useState(false);
  const lineItems = useMemo(() => {
    if (!order) return [];
    if (!order.direct) {
      return orderLineItems;
    }
    if (order.order_type === 'in-store') {
      return [
        { title: "Total", field: "total", bold: true },

      ];
    }
    const lineItems = [
      { title: "Subtotal", field: order.direct ? "customer_subtotal" : "subtotal" },
      { title: order.direct ? "Tax" : "Taxes & Fees", field: order.direct ? "tax" : "order_line_items.Taxes & Fees" },
      order.direct ? { title: "Compliance Fee", field: "service_fee" } : null,
      { title: "Delivery Fee", field: "delivery_fee" },
      { title: "Tip", field: "tip_amount" },
      { title: "Total Charged to Customer", field: "capture_amount", bold: true },
      { title: order.order_status === 5 ? "Voided" : "Refunded", calc: "total_refunded", bold: true, struckthrough: order.order_status === 5 },
      !order.direct && { title: "Saucey withheld", field: 'order_line_items.Saucey withheld' },
      order.direct && { title: "Application Fees", calc: "total_withheld", withholding: true },
      order.direct && { title: "Transaction Fee", calc: "gateway_fee" },
      order.direct && { title: "Total Paid Out", calc: "total_paid_out", bold: true },
      !order.direct && { title: "Deposit Amount", field: 'order_line_items.Estimated Deposit', bold: true }
    ].filter(Boolean);

    return lineItems
  }, [order, orderLineItems])
  const { isMP } = useTheme();

  const withholding = useMemo(() => {
    if (!order) return;
    const lineItems = [
      {
        title: isMP ? "Service fee" : "Compliance Fee",
        field: "service_fee",
      },
      {
        title: isMP ? "Service fee" : "Compliance Fee",
        field: "retailer_paid_service_fee",
      },
      { title: "Delivery Fee", field: "direct_delivery_fee_actual" },
    ];
    if (order.order_type === "delivery" && !order.employee_delivery) {
      lineItems.push({ title: "DSP Tip", field: "tip_amount" });
    }
    lineItems.push({
      title: "Total Withheld",
      calc: "total_withheld",
      bold: true,
    });

    return (
      <div className="flex flex-col w-full">
        <span className="font-bold">Application fee:</span>
        {lineItems?.map((item) => {
          let classname = `flex`;
          if (item.bold) classname += ` font-bold`;
          if (!item.calc && !order[item?.field]) return null;

          return (
            <div key={"cart_" + item.title} className={classname}>
              <div className="flex gap-2 items-center flex-grow">
                {item.title}
              </div>
              <div className={"w-24 justify-end flex " + (item.struckthrough ? "line-through" : "")}>
                {item.calc
                  ? formatValue(calc(item.calc, order))
                  : formatValue(order[item.field])}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [order, isMP]);

  const maxRefundableAmount = useMemo(() => {
    if (!order) return 0;

    // Calculate total captured charges (status 2)
    const totalCaptured =
      order.payments
        ?.filter((payment) => payment.charge_status === 2) // Captured payments
        ?.reduce((sum, payment) => sum + (payment.hold_amount || 0), 0) || 0;

    // Calculate total already refunded (status -1)
    // Only count refunds that have a corresponding successful payment with same payment_intent_id
    const totalRefunded =
      order.payments
        ?.filter((payment) => {
          if (payment.charge_status !== -1) return false; // Must be a refund

          // Check if there's a corresponding successful payment with same payment_intent_id
          let compareField = "payment_intent_id";
          if (payment.payment_intent_id) {
            compareField = "payment_intent_id";
          } else if (payment.frame_charge_intent_id) {
            compareField = "frame_charge_intent_id";
          }
          const hasSuccessfulPayment = order.payments?.some(
            (otherPayment) =>
              otherPayment[compareField] === payment[compareField] &&
              otherPayment.charge_status === 2
          );

          return hasSuccessfulPayment;
        })
        ?.reduce((sum, payment) => sum + (payment.hold_amount || 0), 0) || 0;


    // Use existing withholding calculation
    const totalWithholding = Math.abs(calc("total_withheld", order)) * 100;
    if (order.order_status === 5) {
      return {
        regular: 0,
        fullRefund: 0,
        withholding: 0,
      };
    }

    // Maximum refundable = captured - withholding - already refunded
    const maxRefundableWithoutWithholding = (totalCaptured - totalRefunded - totalWithholding) / 100;
    // totalCapturedDollars - totalWithholding - totalRefundedDollars;
    const maxRefundable = (totalCaptured - totalRefunded) / 100 // Saucey admins can refund withholding too

    return {
      regular: Math.max(0, maxRefundableWithoutWithholding),
      fullRefund: Math.max(0, maxRefundable),
      withholding: Math.max(0, totalWithholding),
    };
  }, [order]);

  // Helper to get the appropriate max refundable amount based on user role
  const getMaxRefundableAmount = () => {
    return maxRefundableAmount.fullRefund;
  };

  useEffect(() => {
    if (!modalOpen) {
      setOrder(null);
      setProcessing(false);
      setLoading(false);
      setProcessing(false);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || !orderID) {
      return;
    }
    setLoading(true);
    // fetch order details
    getOrderByID(orderID)
      .then(async (order) => {
        const res = order.order;
        const foundStore = findMerchantLocationByStoreId(
          merchants,
          res.store_id
        );
        if (
          foundStore &&
          res.store_id != null &&
          String(selectedLocation?.store_id ?? "") !==
            String(res.store_id)
        ) {
          await dispatch(setSelectedMerchant(foundStore.merchant.id));
          dispatch({
            type: "SET_SELECTED_LOCATION",
            payload: foundStore.location.id,
          });
          setOrderFetchKey((k) => k + 1);
          return;
        }
        const lineItems = order.line_items;

        if (isDemoUser) {
          const { first_name, last_name } = getStableFakeName(
            res._id,
            !res.direct
          );
          res.customer.first_name = first_name;
          res.customer.last_name = last_name;
        }
        setOrderLineItems(lineItems);
        setOrder(res);
      })
      .finally(() => {
        setLoading(false);
        setProcessing(false);
      });
  }, [modalOpen, orderID, orderFetchKey, merchants]);

  return (
    <div>
      <ModalBasic
        width={"2xl"}
        classNames={"max-h-[90vh]"}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        title={
          loading
            ? "Loading"
            : "Order Details - " +
            (isMP
              ? order?.easy_id + " (" + order?.order_number + ")"
              : order?.order_number)
        }
      >
        {/* flex center div*/}
        {loading || !order ? (
          <div className="flex flex-col justify-center items-center mt-12 mb-12 ml-4 mr-4 gap-5">
            <svg
              className="animate-spin w-16 h-16 fill-current shrink-0"
              viewBox="0 0 16 16"
            >
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
            Please wait
          </div>
        ) : (
          <div className={`flex flex-col justify-center items-center gap-5`}>
            <div className="flex border-b border-slate-200 dark:border-slate-700 w-full">
              <ul className="text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    setCurrentView("order");
                  }}
                >
                  <span
                    className={
                      "cursor-pointer whitespace-nowrap flex items-center " +
                      (currentView === "order"
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                    }
                  >
                    <FaList
                      className="w-6 h-6
                     shrink-0 fill-current  mr-2"
                    />
                    <span>Order Details</span>
                  </span>
                </li>
                {order?.order_type === "delivery" && (
                  <li
                    className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                    onClick={() => {
                      setCurrentView("delivery");
                    }}
                  >
                    <span
                      className={
                        "cursor-pointer whitespace-nowrap flex items-center " +
                        (currentView === "delivery"
                          ? "text-indigo-500 dark:text-indigo-400"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                      }
                    >
                      {" "}
                      <FaTruck className="w-6 h-6 shrink-0 fill-current mr-2" />
                      <span>Delivery</span>
                    </span>
                  </li>
                )}
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    setCurrentView("payment");
                  }}
                >
                  <CanView directOnly>
                    <span
                      className={
                        "cursor-pointer whitespace-nowrap flex items-center " +
                        (currentView === "payment"
                          ? "text-indigo-500 dark:text-indigo-400"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                      }
                    >
                      {" "}
                      <FaDollarSign className={"w-6 h-6 shrink-0 mr-2"} />
                      <span>Payment</span>
                    </span>
                  </CanView>
                </li>
                <li
                  className="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
                  onClick={() => {
                    setCurrentView("history");
                  }}
                >
                  <span
                    className={
                      "cursor-pointer whitespace-nowrap flex items-center " +
                      (currentView === "history"
                        ? "text-indigo-500 dark:text-indigo-400"
                        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300")
                    }
                  >
                    <FaClock className="w-6 h-6 shrink-0 fill-current mr-2" />
                    <span>History</span>
                  </span>
                </li>
              </ul>
            </div>
            {currentView === "order" && (
              <>
                <div
                  className={`flex ${isSmallSize ? "flex-col" : "flex-row"
                    } justify-between w-full border-b border-slate-200 dark:border-slate-700 pb-2 text-sm`}
                >
                  <div>
                    {order.order_type !== 'in-store' && (
                      <>
                        {!isSmallSize && (
                          <div className="text-slate-400 dark:text-slate-500">
                            Customer
                          </div>
                        )}
                        <div className="font-semibold text-slate-800 dark:text-slate-100">
                          <div className="flex items-center gap-2">
                            <span className="cursor-pointer underline font-semibold">
                              {order.order_type === 'in-store' ? 'In-Store' : `${order.customer.first_name} ${order.customer.last_name}`}
                            </span>
                            <CanView directOnly sauceyAdminAlwaysShow>
                              <button
                                onClick={() => setCustomerModalOpen(true)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                title="Open customer profile"
                              >
                                <FaArrowUpRightFromSquare className="w-3 h-3" />
                              </button>
                            </CanView>
                          </div>
                          <div>{formatPhoneNumber(order.customer.mobile)}</div>
                          <div>{order.customer.email}</div>
                          <div>
                            DOB: {order.customer.birthday}{" "}
                            {order.customer.verification_status === "PASSED" ? (
                              <Badge slim color={"green"}>
                                Verified
                              </Badge>
                            ) : (
                              <Badge slim color={"red"}>
                                Self-reported
                              </Badge>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                  </div>
                  <div className={isSmallSize ? "" : "text-right"}>
                    {!isSmallSize && (
                      <div className="text-slate-400 dark:text-slate-500">
                        Order Detail
                      </div>
                    )}
                    <div className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                      <div>
                        {order.order_type || "delivery"}{" "}
                        <Badge
                          customColor={statusColor(
                            OrderStatusInverseEnum[order.order_status]
                          )}
                          size="xs"
                          slim
                        >
                          {OrderStatusInverseEnum[order.order_status]
                            .replace("_", " ")
                            .replace("IN PROGRESS", "EN ROUTE")}
                        </Badge>
                      </div>
                      {order.pos_order && (
                        <div className="mt-1 text-xs font-normal text-slate-500 dark:text-slate-400 flex items-center justify-end gap-1">
                          {(() => {
                            const key = order.pos_order.provider?.toLowerCase();
                            const iconSrc = key ? posProviderIcons[key] : null;
                            if (iconSrc) {
                              return (
                                <img
                                  src={iconSrc}
                                  alt={`${order.pos_order.provider} POS`}
                                  className="w-3.5 h-3.5"
                                />
                              );
                            }
                            return (
                              <FaReceipt className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                            );
                          })()}
                          <span>
                            {order.pos_order.external_id
                              ? ` ${order.pos_order.external_id}`
                              : ""}
                          </span>
                        </div>
                      )}
                    </div>
                    {order.fraud_eval && (
                      <CanView directOnly sauceyAdminAlwaysShow>
                        <div
                          className="flex items-center justify-end gap-2 cursor-pointer mt-1"
                          onClick={() => setViewFraudEval(order.fraud_eval)}
                        >
                          <Badge
                            color={order.fraud_eval.fraud_level}
                            size="xs"
                          >
                            <div className="flex items-center gap-2">
                              <FaMagnifyingGlass className="w-4 h-4" />
                              Fraud Score: {order.fraud_eval.score}
                              <svg
                                className={
                                  "fill-current text-slate-400 dark:text-slate-500 w-4 h-4"
                                }
                                viewBox="0 0 16 16"
                              >
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
                              </svg>
                            </div>
                          </Badge>
                        </div>
                      </CanView>
                    )}
                  </div>
                </div>

                {/*items*/}
                <div
                  className={
                    "w-full overflow-auto mb-4 min-h-[300px] max-h-[calc(100%-300px)]"
                  }
                >
                  {order?.products.map((product, idx) => {

                    let price;
                    let unitPrice;
                    let productDiscountPrice = 0;
                    let bulkPricing = null;
                    let bulkPricingDescription = null;
                    let bulkPricingDiscount = 0;
                    if (order.direct) {
                      price =
                        (product.qty ?? product.quantity) *
                        (product.discount_price ??
                          product.price ??
                          product.sell_price);
                      unitPrice = product.pre_tax_price ?? product.price ?? product.sell_price
                      productDiscountPrice = product.pre_tax_price !== product.discount_price ? product.discount_price : 0;
                    } else {
                      price = product.buy_price * product.quantity // non discounted products don't have retailer_subtotal, don't ask me why

                      unitPrice = product.buy_price
                      if (product.retailer_subtotal < price && product.package_qty > 1) {
                        bulkPricing = product.retailer_subtotal
                        bulkPricingDescription = `Buy ${product.package_qty} save ${formatValue((product.buy_price - product.package_buy_price) * product.package_qty)}`;
                        bulkPricingDiscount = product.buy_price * product.quantity - product.retailer_subtotal;
                      } else {
                        bulkPricing = null;
                        bulkPricingDescription = null;
                        bulkPricingDiscount = 0;
                      }
                    }

                    const rowColor =
                      idx % 2 === 0 ? "bg-slate-100 dark:bg-slate-700" : "";
                    return (
                      <div
                        className={`relative py-1 px-1 flex flex-row  w-full flex-1 ${rowColor} ${isSmallSize ? "text-sm" : ""
                          }`}
                        key={product.sku_id}
                      >
                        <div className="w-16 mr-4">
                          <img
                            src={product.image}
                            alt={product.title}
                            className="h-16 w-16 object-cover rounded"
                          />
                        </div>
                        <div
                          className={
                            "flex flex-grow justify-center flex-col" +
                            (isSmallSize ? " w-full" : "")
                          }
                        >
                          <div className="font-semibold text-slate-800 dark:text-slate-100">
                            {product.title} {product.subtitle}
                          </div>
                          <div className="text-slate-400 dark:text-slate-500">
                            {product.volume}
                          </div>
                          {bulkPricingDescription && (
                            <div className={"text-slate-400 dark:text-slate-500 " + (isSmallSize ? "" : "text-right")}>
                              <FaTag className="inline-block mr-1" />{bulkPricingDescription}
                              {isSmallSize && <span className="text-slate-400 dark:text-slate-500"> =
                                <span className="font-bold ml-1">
                                  -{formatValue(bulkPricingDiscount)}
                                </span></span>}
                            </div>
                          )}
                          {bulkPricingDiscount > 0 && !isSmallSize && (<div > &nbsp; </div>)}

                          {isSmallSize && (
                            <div className="text-slate-400 dark:text-slate-500">
                              {product.qty ?? product.quantity} @{" "}
                              <span
                                className={
                                  productDiscountPrice ? "line-through" : ""
                                }
                              >
                                {formatValue(
                                  unitPrice
                                )}
                              </span>
                              {Boolean(productDiscountPrice) && (
                                <span className="text-red-500">
                                  {" "}
                                  {formatValue(productDiscountPrice)}
                                </span>
                              )}{" "}
                              = <span className={`${bulkPricing ? "line-through" : "font-bold"} `}>{formatValue(price)}</span>
                              {bulkPricing && (
                                <span className="font-bold ml-1">
                                  {formatValue(bulkPricing)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex justify-center flex-col text-right">
                          <div
                            className={`font-semibold text-slate-800 dark:text-slate-100 flex justify-end items-center ${isSmallSize ? "w-[2rem]" : "w-24"
                              }`}
                          >

                            <span>
                              {isSmallSize && "x "}
                              {product.qty ?? product.quantity}
                            </span>
                            {!isSmallSize &&
                              <>
                                @
                                <span
                                  className={
                                    productDiscountPrice ? "line-through ml-1" : "ml-1"
                                  }
                                >
                                  {formatValue(
                                    unitPrice
                                  )}
                                </span>
                                {Boolean(productDiscountPrice) && (
                                  <span className="text-red-500">
                                    {" "}
                                    {formatValue(productDiscountPrice)}
                                  </span>
                                )}{" "}
                              </>}
                          </div>

                          {!isSmallSize && (
                            <div className={`text-slate-400 dark:text-slate-500 ${bulkPricing ? "line-through" : "font-bold"}`}>
                              {isNaN(price) ? "-" : formatValue(price)}
                            </div>
                          )}
                          {!isSmallSize && bulkPricingDiscount > 0 && (
                            <div className="text-slate-400 dark:text-slate-500">
                              - {formatValue(bulkPricingDiscount)}
                            </div>
                          )}
                          {!isSmallSize && bulkPricing && (
                            <div className={`text-slate-400 dark:text-slate-500 font-bold`}>
                              {formatValue(bulkPricing)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className={"flex flex-1 items-end flex-col"}>
                    {(order?.direct ? lineItems : orderLineItems).map((item) => {
                      if (isMP) {
                        item.title = item.title.replace(
                          "Compliance Fee",
                          "Service fee"
                        );
                      }

                      let row = null;
                      if (item.field?.includes("order_line_items.")) {
                        row = orderLineItems.find(lineItem => lineItem.title === item.field.split("order_line_items.")[1]);
                      }

                      if (!item.calc && !item.value && order[item.field] === undefined && !row)
                        return null;
                      if (!item.calc && !item.value && !order[item.field] && !row) return null;



                      // Only show "Refunded" line item if there are actual refunds
                      if (item.calc === "total_refunded") {
                        const refundedAmount = Math.abs(
                          calc("total_refunded", order)
                        );
                        if (refundedAmount <= 0 && order.order_status !== 5) return null;
                      }
                      let classname = `flex`;
                      if (item.bold) {
                        classname += ` font-bold`;
                      }
                      return (
                        <CanView permissions={item.permissions || ""}>
                          <div
                            key={"cartlineitem_" + (item.title || item.field || item.calc)}
                            className={classname}
                          >
                            <div className="flex gap-2 items-center">
                              {item.withholding && (
                                <Tooltip size={"xl"} position={"left"}>
                                  {withholding}
                                </Tooltip>
                              )}
                              {item.tooltip && <Tooltip size={"xl"} position={"left"}>
                                {item.tooltip}
                              </Tooltip>}
                              {item.admin && <FaCrown className="w-4 h-4 text-amber-500" />}
                              {item.title}
                            </div>
                            <div className={"w-24 justify-end flex " + (item.struckthrough ? "line-through" : "")}>
                              {item.value ? formatValue(item.value) : (item.calc
                                ? formatValue(calc(item.calc, order))
                                : formatValue(row ? row['value'] : order[item.field]))}
                            </div>
                          </div>
                        </CanView>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            {currentView === "delivery" && (
              <>
                <div
                  className={`flex ${isSmallSize ? "flex-col" : "flex-row"
                    } justify-between w-full border-b border-slate-200 dark:border-slate-700 pb-2`}
                >
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">
                      Customer
                    </div>
                    <div className="font-semibold text-slate-800 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="cursor-pointer underline font-semibold">
                          {order.order_type === 'in-store' ? 'In-Store' : `${order.customer.first_name} ${order.customer.last_name}`}
                        </span>
                        <button
                          onClick={() => setCustomerModalOpen(true)}
                          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                          title="Open customer profile"
                        >
                          <FaArrowUpRightFromSquare className="w-3 h-3" />
                        </button>
                      </div>
                      {order.external_loyalty_program && (
                        <Badge color={"green"}>
                          {order.external_loyalty_program.program} Member
                        </Badge>
                      )}
                      <div>{formatPhoneNumber(order.customer.mobile)}</div>
                      <div>{order.customer.email}</div>
                      <div>
                        DOB: {order.customer.birthday}{" "}
                        {order.customer.verification_status === "PASSED" ? (
                          <Badge slim color={"green"}>
                            Verified
                          </Badge>
                        ) : (
                          <Badge slim color={"red"}>
                            Self-reported
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <CanView permissions="saucey_admin">
                    <div>
                      <div className="text-slate-400 dark:text-slate-500">
                        Store Details
                      </div>
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        <div>{order.vendor?.name}</div>
                        <div>{order.vendor?.address}</div>
                        <div>
                          {order.vendor?.city}, {order.vendor?.state}{" "}
                          {order.vendor?.zip}
                        </div>
                      </div>
                    </div>
                  </CanView>
                  <CanView directOnly sauceyAdminAlwaysShow>
                    <div>
                      <div className="text-slate-400 dark:text-slate-500">
                        Delivery Details
                      </div>
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        <div>{order.delivery_address_detail?.address}</div>
                        <div>
                          {order.delivery_address_detail?.city},{" "}
                          {order.delivery_address_detail?.state}{" "}
                          {order.delivery_address_detail?.zip}
                        </div>
                      </div>
                    </div>
                  </CanView>
                </div>
                {order?.delivery_notes && (
                  <div className="justify-start items-start w-full border-b border-slate-200 dark:border-slate-700 pb-2">
                    <span className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                      <FaComment className="" /> Customer Notes
                    </span>
                    <div className="text-sm">{order?.delivery_notes}</div>
                  </div>
                )}
                {order?.provider_delivery && (
                  <div className="justify-start items-start w-full pb-2 flex flex-row justify-between">
                    <div>
                      <div className="text-slate-400 dark:text-slate-500 text-md">
                        Tracking
                      </div>
                      <div className="text-sm flex flex-col">
                        {!order?.provider_delivery.delivery_tracking_url ? (
                          "No tracking available yet"
                        ) : (
                          <SecondaryButton
                            size={"sm"}
                            onClick={() => {
                              window.open(
                                order?.provider_delivery.delivery_tracking_url,
                                "_blank"
                              );
                            }}
                          >
                            Track on {order?.provider_delivery.provider?.name}
                          </SecondaryButton>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 dark:text-slate-500 text-md">
                        Status
                      </div>
                      <div className="text-sm flex flex-col capitalize">
                        {order?.provider_delivery?.status?.replace("_", " ")}
                      </div>
                    </div>
                    {order?.driver?.first_name && (
                      <div>
                        <div className="text-slate-400 dark:text-slate-500">
                          Courier
                        </div>
                        <div className="text-sm">
                          {order.driver.first_name} {order.driver.last_name}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {currentView === "payment" && (
              <div className="flex flex-col w-full pb-2">
                <ModalBasic
                  width={"md"}
                  modalOpen={Boolean(viewPaymentIntentId)}
                  setModalOpen={setViewPaymentIntentId}
                >
                  <PaymentModal
                    paymentIntentID={viewPaymentIntentId}
                    setClose={() => setViewPaymentIntentId(null)}
                  />
                </ModalBasic>
                <table className="table-auto w-full dark:text-slate-300">
                  <thead className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <tr>
                      <th className="text-left">
                        {(() => {
                          if (order.payments?.[0]?.frame_charge_intent_id) {
                            return "Frame ID";
                          } else if (order.payments?.[0]?.payment_intent_id) {
                            return "Stripe ID";
                          } else if (order.payments?.[0]?.nmi_transaction_id) {
                            return "NMI ID";
                          }
                          return "Payment ID";
                        })()}
                      </th>
                      <th className="text-left">Amount</th>
                      <th className="text-left">Status</th>
                      <th className="text-left">Created</th>
                      <th className="text-left">View</th>
                    </tr>
                  </thead>
                  {order.payments?.map((payment) => (
                    <tr key={payment._id} className="text-sm">
                      <td className="whitespace-nowrap">
                        {payment.nmi_transaction_id ||
                          payment.payment_intent_id ||
                          payment.frame_refund_id?.slice(-10) ||
                          payment.frame_charge_intent_id?.slice(-10)}
                      </td>
                      <td className="whitespace-nowrap">
                        {formatValue(
                          (payment.capture_amount || payment.hold_amount) / 100
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {(() => {
                          if (order.order_status === 5) {
                            return <Badge color="red">Voided</Badge>;
                          }
                          switch (payment.charge_status) {
                            case -1:
                              return (
                                <div className="flex items-center gap-2">
                                  <Badge color="red">Refunded</Badge>
                                  {payment.description && (
                                    <Tooltip size="lg" position="top">
                                      <div className="text-sm whitespace-pre-line">
                                        {payment.description}
                                      </div>
                                    </Tooltip>
                                  )}
                                </div>
                              );
                            case 1:
                              return <Badge color="yellow">Authorized</Badge>;
                            case 2:
                              return <Badge color="green">Captured</Badge>;
                          }
                        })()}
                      </td>
                      <td className="whitespace-nowrap">
                        {moment(payment.created_at).format("lll")}
                      </td>
                      <td className="whitespace-nowrap">
                        <SecondaryButton
                          naked
                          size={"sm"}
                          onClick={() => {
                            if (payment.payment_intent_id) {
                              setViewPaymentIntentId(payment.payment_intent_id);
                            } else if (payment.nmi_transaction_id) {
                              window.open(
                                `https://blvd.transactiongateway.com/merchants/reports.php?Action=Details&transaction_type=cc&merchant_id=${payment.nmi_gateway_id}&transaction=${payment.nmi_transaction_id}&report_id=0`
                              );
                            } else if (payment.frame_charge_intent_id) {
                              window.open(
                                `https://app.framepayments.com/transactions/${payment.frame_charge_intent_id}`,
                                "_blank"
                              );
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="icon icon-tabler icon-tabler-eye cursor-pointer stroke-current"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                          </svg>
                        </SecondaryButton>
                      </td>
                    </tr>
                  ))}
                </table>
              </div>
            )}
            {currentView === "history" && (
              <OrderHistoryTimeline
                orderId={orderID}
                timezone={selectedLocation?.tz}
                isActive={currentView === "history" && modalOpen}
                refreshKey={historyRefreshKey}
              />
            )}
            <div className="flex w-full flex-1 justify-between">
              {order.order_type !== 'in-store' && (
                <>
                  <CanView directOnly permissions="manager">
                    <SecondaryDangerButton
                      onClick={() => {
                        setRefundModalOpen(true);
                      }}
                      disabled={order.order_status === 5}
                    >
                      Issue Refund
                    </SecondaryDangerButton>
                  </CanView>
                </>
              )}
              <SecondaryButton
                disabled={processing}
                loading={processing}
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Close
              </SecondaryButton>
            </div>
          </div>
        )}
      </ModalBasic>
      {/* Customer Detail Modal */}
      <CustomerDetailModal
        modalOpen={customerModalOpen}
        setModalOpen={setCustomerModalOpen}
        customerEmail={order?.customer?.email}
        title="Customer Profile"
      />

      <ModalBasic
        width="2xl"
        modalOpen={Boolean(viewFraudEval)}
        setModalOpen={() => setViewFraudEval(null)}
        title="Fraud Evaluation Details"
      >
        {viewFraudEval && (
          <div className="space-y-6">
            {/* Overall Score and Decision */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Overall Assessment
                </h3>
                <div className="flex items-center gap-3">
                  <Badge color={viewFraudEval.fraud_level || "gray"}>
                    <div className="flex items-center gap-2">
                      <PiDetectiveBold className="w-5 h-5" />
                      Score: {viewFraudEval.score}
                    </div>
                  </Badge>
                  <Badge
                    color={
                      viewFraudEval.decision === "approve"
                        ? "green"
                        : viewFraudEval.decision === "hold"
                          ? "yellow"
                          : viewFraudEval.decision === "auto-rejected"
                            ? "red"
                            : "gray"
                    }
                  >
                    {((decision) => {
                      switch (decision) {
                        case "auto-rejected":
                          return "Auto-Rejected";
                        case "hold":
                          return "Hold";
                        case "approve":
                          return "Not Flagged";
                        default:
                          return (
                            decision.charAt(0).toUpperCase() + decision.slice(1)
                          );
                      }
                    })(viewFraudEval.decision)}
                  </Badge>
                </div>
              </div>

              {/* Risk Level Description */}
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {viewFraudEval.fraud_level === "green" && (
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Low Risk - Order appears legitimate</span>
                  </div>
                )}
                {viewFraudEval.fraud_level === "orange" && (
                  <div className="flex items-center gap-2">
                    <FaTriangleExclamation className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span>Medium Risk - Review recommended</span>
                  </div>
                )}
                {viewFraudEval.fraud_level === "red" && (
                  <div className="flex items-center gap-2">
                    <FaCircleXmark className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span>High Risk - Proceed with caution</span>
                  </div>
                )}
                {!viewFraudEval.fraud_level && (
                  <div className="flex items-center gap-2">
                    <FaCircleInfo className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span>Risk level not available</span>
                  </div>
                )}
              </div>

              {/* Decision Description */}
              <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                {viewFraudEval.decision === "approve" && (
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Order not flagged</span>
                  </div>
                )}
                {viewFraudEval.decision === "hold" && (
                  <div className="flex items-center gap-2">
                    <FaTriangleExclamation className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span>Order held for manual review</span>
                  </div>
                )}
                {viewFraudEval.decision === "auto-rejected" && (
                  <div className="flex items-center gap-2">
                    <FaCircleXmark className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span>Order automatically rejected due to fraud risk</span>
                  </div>
                )}
                {viewFraudEval.decision === "unavailable" && (
                  <div className="flex items-center gap-2">
                    <FaCircleInfo className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span>Fraud evaluation unavailable</span>
                  </div>
                )}
              </div>
            </div>

            {/* Violations */}
            {viewFraudEval.violations &&
              viewFraudEval.violations.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                    Detected Issues ({viewFraudEval.violations.length})
                  </h3>
                  <div className="space-y-3">
                    {viewFraudEval.violations.map((violation, index) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${violation.score >= 8
                                ? "bg-red-500"
                                : violation.score >= 5
                                  ? "bg-yellow-500"
                                  : "bg-green-500"
                                }`}
                            ></div>
                            <span className="font-medium text-slate-800 dark:text-slate-100">
                              {violation.criterion
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                          <Badge
                            color={
                              violation.score >= 8
                                ? "red"
                                : violation.score >= 5
                                  ? "yellow"
                                  : "green"
                            }
                            size="sm"
                          >
                            Score: {violation.score}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {violation.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* No Violations Message */}
            {(!viewFraudEval.violations ||
              viewFraudEval.violations.length === 0) && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-green-800 dark:text-green-200 font-medium">
                      No fraud indicators detected
                    </span>
                  </div>
                </div>
              )}

            {/* Disclaimer */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <FaTriangleExclamation className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                    Disclaimer
                  </h4>
                  <p className="text-yellow-800 dark:text-yellow-200 text-xs">
                    This fraud evaluation is a tool to assist in decision-making
                    and should not be the sole factor in determining order
                    legitimacy. Always use your best judgment and consider
                    additional factors when processing orders. Fraud patterns
                    and risk factors may change over time.{" "}
                    <strong>
                      You are solely responsible for the final decision
                      regarding order approval or rejection.
                    </strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons for Direct Orders with Orange/Red Fraud Level */}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <SecondaryButton onClick={() => setViewFraudEval(null)}>
                Close
              </SecondaryButton>

            </div>
          </div>
        )}
      </ModalBasic>
      {/* Refund Modal */}
      <ModalBasic
        width="md"
        modalOpen={refundModalOpen}
        setModalOpen={setRefundModalOpen}
        title="Issue Refund"
        titleBarClassName="bg-red-400"
      >
        <div className="space-y-4">
          {!order?.stripe_charge_id &&
            !order?.stripe_payment_intent_id &&
            !order?.frame_charge_intent_id && 
            !order?.nmi_transaction_id ? (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <div className="font-semibold mb-2">Manual Refund Required</div>
                <div>
                  Only Stripe payments can be refunded through this system.
                  Please manually issue refunds on your payment platform for
                  this order.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                You can issue a refund up to{" "}
                {formatValue(maxRefundableAmount.fullRefund)}
              </div>

              <div className="text-sm text-slate-600 dark:text-slate-400">
                If you refund more than {formatValue(maxRefundableAmount.regular)}, the remaining amount will be debited from your merchant account to pay for Saucey's withholdings.
              </div>

              {/* <CanView permissions="saucey_admin">
                <div className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <FaCrown className="text-amber-500" />
                  Refund up to {formatValue(maxRefundableAmount.sauceyAdmin)} as
                  a Saucey Admin
                </div>
              </CanView> */}

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Refund Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={getMaxRefundableAmount()}
                  value={refundAmount}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Only allow up to 2 decimal places
                    if (value.includes(".")) {
                      const parts = value.split(".");
                      if (parts[1] && parts[1].length > 2) {
                        value = parts[0] + "." + parts[1].substring(0, 2);
                      }
                    }

                    setRefundAmount(value);
                    setRefundError("");
                    // Reset confirmation if amount changes
                    if (withholdingConfirmed) {
                      setWithholdingConfirmed(false);
                    }
                  }}
                  className="form-input w-full"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Reason for Refund *
                </label>
                <select
                  value={refundReason}
                  onChange={(e) => {
                    setRefundReason(e.target.value);
                    setCustomReason(""); // Clear custom reason when changing selection
                    setRefundError("");
                  }}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-700 dark:text-slate-100"
                >
                  <option value="">Select a reason</option>
                  <option value="customer_complaint">Customer Complaint</option>
                  <option value="product_defect">Product Defect</option>
                  <option value="wrong_item">Wrong Item Delivered</option>
                  <option value="order_cancellation">Order Cancellation</option>
                  <option value="duplicate_charge">Duplicate Charge</option>
                  <option value="delivery_issue">Delivery Issue</option>
                  <option value="missing_product">Missing Product</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {refundReason === "other" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Please specify the reason *
                  </label>
                  <input
                    type="text"
                    value={customReason}
                    onChange={(e) => {
                      setCustomReason(e.target.value);
                      setRefundError("");
                    }}
                    className="form-input w-full"
                    placeholder="Enter the reason for this refund"
                    maxLength={255}
                  />
                </div>
              )}

              {refundError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {refundError}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <SecondaryButton
                  onClick={() => {
                    setRefundModalOpen(false);
                    setRefundAmount("");
                    setRefundReason("");
                    setCustomReason("");
                    setRefundError("");
                    setWithholdingConfirmed(false);
                    setPendingRefundAmount(null);
                  }}
                >
                  Cancel
                </SecondaryButton>
                <SecondaryDangerButton
                  holdToContinue={true}
                  loading={refundLoading}
                  firstClickAction={async () => {
                    const amount = parseFloat(refundAmount);

                    // Validation - return false to prevent hold state if validation fails
                    if (!refundAmount || isNaN(amount) || amount <= 0) {
                      setRefundError("Please enter a valid refund amount");
                      return false;
                    }

                    if (amount > getMaxRefundableAmount()) {
                      setRefundError(
                        `Refund amount cannot exceed ${formatValue(
                          getMaxRefundableAmount()
                        )}`
                      );
                      return false;
                    }

                    if (!refundReason) {
                      setRefundError("Please select a reason for the refund");
                      return false;
                    }

                    if (refundReason === "other" && !customReason.trim()) {
                      setRefundError(
                        "Please specify the reason for the refund"
                      );
                      return false;
                    }

                    // Check if refunding more than amount without application fee
                    if (amount > maxRefundableAmount.regular && !withholdingConfirmed) {
                      setPendingRefundAmount(amount);
                      setWithholdingWarningModalOpen(true);
                      return false; // Prevent hold state until user confirms
                    }

                    // Clear any previous errors if validation passes
                    setRefundError("");
                    // Reset confirmation state after use
                    setWithholdingConfirmed(false);
                    return true; // Allow hold state to proceed
                  }}
                  onClick={async () => {
                    const amount = parseFloat(refundAmount);

                    try {
                      setRefundLoading(true);

                      // Convert amount to cents
                      const amountInCents = Math.round(amount * 100);

                      // Use custom reason if "other" is selected, otherwise use the selected reason
                      const finalReason =
                        refundReason === "other"
                          ? customReason.trim()
                          : refundReason;

                      const result = await refundOrder(
                        order._id,
                        amountInCents,
                        finalReason
                      );

                      // Show success toast
                      dispatch(
                        addToast({
                          type: "success",
                          message: `Refund of ${formatValue(
                            amount
                          )} processed successfully`,
                          timer: 5,
                        })
                      );

                      forceReload();
                      // Success - reload order data to show new refund
                      setLoading(true);
                      try {
                        const updatedOrder = await getOrderByID(orderID);
                        setOrder(updatedOrder.order);
                      } catch (error) {
                      } finally {
                        setLoading(false);
                      }

                      // Refresh history after a refund so the timeline updates.
                      setHistoryRefreshKey((prev) => prev + 1);

                      // Close modal and reset state
                      setRefundModalOpen(false);
                      setRefundAmount("");
                      setRefundReason("");
                      setCustomReason("");
                      setRefundError("");
                      setWithholdingConfirmed(false);
                      setPendingRefundAmount(null);
                    } catch (error) {
                      let message =
                        "Failed to process refund. Please try again.";
                      try {
                        const serverMessage = error.response?.data?.message;
                        if (serverMessage) {
                          message = serverMessage;
                        }
                      } catch { }
                      setRefundError(message);
                    } finally {
                      setRefundLoading(false);
                    }
                  }}
                >
                  {`Refund ${refundAmount
                    ? formatValue(parseFloat(refundAmount))
                    : formatValue(0)
                    }`}
                </SecondaryDangerButton>
              </div>
            </>
          )}

          {!order?.stripe_charge_id &&
            !order?.stripe_payment_intent_id &&
            !order?.frame_charge_intent_id && (
              <div className="flex justify-end pt-4">
                <SecondaryButton
                  onClick={() => {
                    setRefundModalOpen(false);
                  }}
                >
                  Close
                </SecondaryButton>
              </div>
            )}
        </div>
      </ModalBasic>
      {/* Withholding Warning Modal */}
      <ModalBasic
        width="md"
        modalOpen={withholdingWarningModalOpen}
        setModalOpen={setWithholdingWarningModalOpen}
        title="Refund Notice"
      >
        <div className="space-y-4 ">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
            <div className="flex items-start gap-3">
              <FaTriangleExclamation className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <div className="font-semibold mb-2">Withholding Notice</div>
                <div>
                  You are refunding more than the paid out amount to your merchant account.
                  The remaining amount of{" "}
                  <strong>
                    {formatValue(
                      pendingRefundAmount
                        ? pendingRefundAmount - maxRefundableAmount.regular
                        : 0
                    )}
                  </strong>{" "}
                  will be debited from your merchant account to cover Saucey's
                  withholdings.
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton
              onClick={() => {
                setWithholdingWarningModalOpen(false);
                setPendingRefundAmount(null);
                setWithholdingConfirmed(false);
              }}
            >
              Cancel
            </SecondaryButton>
            <SecondaryDangerButton
              onClick={() => {
                setWithholdingConfirmed(true);
                setWithholdingWarningModalOpen(false);
                setPendingRefundAmount(null);
              }}
            >
              Proceed with Refund
            </SecondaryDangerButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default PastOrderModal;
